/**
 * Helper: Utils
 * ------------------------------------------------------------------------------
 * Karmoon utility functions.
 *
 * @namespace utils
 */

 /**
 * Get unique objects from object literal.
 * @param {Object} object - Array/Object to remove duplicates from.
 * @returns {Object}
 */
  export function unique(object) {
    return object.filter((value, index, self) => self.indexOf(value) === index);
  }
  
  /**
   * Get the difference of two arrays.
   * @param {Array} array - An array containing two arrays to diff.
   * @returns {Array}
   */
  export function difference(array1, array2) {
    return array1.concat(array2).filter((value, index, self) => {
      return self.indexOf(value) === self.lastIndexOf(value);
    });
  }
  
  /**
   * Combine two objects using properties as the override.
   * @param {object} defaults - Defaults options defined in script.
   * @param {object} properties - Options defined by user.
   * @return {object} - Defaults modified options.
   */
  export function extendDefaults(defaults, properties) {
    // eslint-disable-next-line no-restricted-syntax
    for (const property in properties) {
      if (properties != null && typeof properties !== 'undefined') {
        defaults[property] = properties[property];
      }
    }
  
    return defaults;
  }
  
  /**
   * Get all siblings of an element.
   * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com.
   * @param {Node} element - The element you want get the siblings of.
   * @returns {Array}
   */
  export function getSiblings(element) {
    return Array.prototype.filter.call(element.parentNode.children, (sibling) => {
      return sibling !== element;
    });
  }
  
  /**
   * Serialize all form data into a query string.
   * @param {Node} form - The form to serialize.
   * @returns {String}
   */
  export function serialize(form) {
    const serialized = [];
  
    /**
     * Loop through each field in the form.
     */
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < form.elements.length; i++) {
      const field = form.elements[i];
  
      // Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
      if (
        !field.name ||
        field.disabled ||
        field.type === 'file' ||
        field.type === 'reset' ||
        field.type === 'submit' ||
        field.type === 'button'
      ) {
        // eslint-disable-next-line no-continue
        continue;
      }
  
      /**
       * If a multi-select, get all selections.
       */
      if (field.type === 'select-multiple') {
        // eslint-disable-next-line no-plusplus
        for (let counter = 0; counter < field.options.length; counter++) {
          if (!field.options[counter].selected) {
            // eslint-disable-next-line no-continue
            continue;
          }
  
          serialized.push(
            `${encodeURIComponent(field.name)}=${encodeURIComponent(
              field.options[counter].value
            )}`
          );
        }
      } else if (
        (field.type !== 'checkbox' && field.type !== 'radio') ||
        field.checked
      ) {
        serialized.push(
          `${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`
        );
      }
    }
  
    return serialized.join('&');
  }
  
  /**
   * Shortcut function to add an event listener.
   * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
   * @param {String} event -The event type.
   * @param {Node} elem - The element to attach the event to (optional, defaults to window).
   * @param {Function} callback - The callback to run on the event.
   * @param {Boolean} capture - If true, forces bubbling on non-bubbling events.
   */
  export function on(event, elem = window, callback, capture = false) {
    /**
     * If only a string is passed into the element parameter.
     */
    if (typeof elem === 'string') {
      document.querySelector(elem).addEventListener(event, callback, capture);
      return;
    }
  
    /**
     * If an element is not defined in parameters, then shift callback across.
     */
    if (typeof elem === 'function') {
      window.addEventListener(event, elem);
      return;
    }
  
    /**
     * Default listener.
     */
    elem.addEventListener(event, callback, capture);
  }
  
  /**
   * Browser detect and return a string
   * - https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
   * @returns {String} browser string.
   */
  export function getBrowser() {
    const { userAgent } = window.navigator;
  
    // The order matters here, and this may report false positives for unlisted browsers.
    if (userAgent.indexOf('Firefox') > -1) {
      // 'Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0'
      return 'Firefox';
    }
    if (userAgent.indexOf('Opera') > -1) {
      return 'Opera';
    }
    if (userAgent.indexOf('Trident') > -1) {
      // 'Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko'
      return 'Internet Explorer';
    }
    if (userAgent.indexOf('Edge') > -1) {
      // 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299'
      return 'Edge';
    }
    if (userAgent.indexOf('Chrome') > -1) {
      // 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36'
      return 'Chrome';
    }
    if (userAgent.indexOf('Safari') > -1) {
      // 'Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306'
      return 'Safari';
    }
  
    return 'unknown';
  }
  
  /**
   * Debounce functions for better performance
   * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
   * @param {Function} callback The function to debounce.
   * @param {Number} wait - The amount of time before debounce call is triggered.
   * @param {Boolean} immediate - Trigger the immediately.
   */
  export function debounce(callback, wait, immediate) {
    let timeout = null;
  
    return function (...args) {
      const later = function () {
        timeout = null;
        if (!immediate) {
          callback.apply(this, args);
        }
      };
  
      const callNow = immediate && !timeout;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(later, wait);
  
      if (callNow) {
        callback.apply(this, args);
      }
    };
  }
  